import type { RuleCode } from "./rule-codes.generated";

/**
 * What a landing page's images show where its own content should be: the
 * landing-page read's typed verdict on them (python-api
 * `services/lp_asset_analysis_service.py` `CaptureShows`, the same values),
 * given the address the page was asked for and the one its capture ended on.
 * Only `page` is the page. Any other value makes the capture a failed one
 * whose cause is that value (Nest `recordCaptureNotThePage`), so readiness
 * reads it as it reads every failed capture (wave F L6: a blank or redirected
 * capture used to count as a success whenever its text cleared a length
 * guess, LP-23's blank Jersey Mike's and Firehouse's store locator).
 */
export const CAPTURE_SHOWS = ["page", "blank", "blocker", "other_page"] as const;
export type CaptureShows = (typeof CAPTURE_SHOWS)[number];

export function isCaptureShows(value: unknown): value is CaptureShows {
  return typeof value === "string" && (CAPTURE_SHOWS as ReadonlyArray<string>).includes(value);
}

/**
 * The rule catalog entry whose user line says why a page's capture failed,
 * by the capture's typed reason: python-api `survey/media/capture.py` reason
 * codes, the landing-page read's `captureShows` when it is not the page, and
 * the Save's own `capture_returned_no_image` / `enrichment_transport_failed`.
 * The one owner of that line: the link dialog and the composer say it when a
 * capture fails (`friendlyCaptureError`), readiness raises it on a saved page
 * that has no capture (`ordinalAssetContentIssues`), and the chat reply reads
 * it in the page's facts (`turn-reply.ts`, `notCaptured`). Wave E L-CAP:
 * LP-26's pages were too tall and LP-24's came back empty; the chat never said
 * so and the editor showed a generic note, the real reason only in the link
 * dialog.
 */
const PAGE_CAPTURE_LINE_BY_REASON: Readonly<Record<string, RuleCode>> = {
  capture_too_large: "page_capture_too_large",
  full_page_changed: "page_capture_changed",
  http_error: "page_capture_http_error",
  blocker: "page_capture_bot_wall",
  blank: "page_capture_empty",
  other_page: "page_capture_other_page",
  timeout: "page_capture_timeout",
  nav_timeout: "page_capture_timeout",
  unreachable: "page_capture_unreachable",
  ssrf_blocked: "page_capture_url_not_allowed",
  invalid_url: "page_capture_invalid_url",
  // Causes stored by drafts saved before wave F L6, which no capture or read
  // writes any more: each keeps the line it was shown with.
  empty: "page_capture_empty",
  bot_wall: "page_capture_bot_wall",
  full_page_too_tall: "page_capture_too_large",
};

/**
 * The catalog code for a capture's failure reason. A reason with no line of
 * its own (capture's `error` and `capture_dimensions_invalid`, the Save's
 * transport failures, a reason a newer capture adds) is the catalog's
 * `page_capture_failed`, as the link dialog said "Could not capture this
 * page" for every other reason.
 */
export function pageCaptureLineCode(reason: unknown): RuleCode {
  return (
    (typeof reason === "string" && Object.prototype.hasOwnProperty.call(PAGE_CAPTURE_LINE_BY_REASON, reason)
      ? PAGE_CAPTURE_LINE_BY_REASON[reason]
      : undefined) ?? "page_capture_failed"
  );
}

/**
 * The line for the same cause when nothing was added: the Add by URL dialog,
 * the collect intake and an item's attach refuse an ad whose capture failed,
 * and there is no page to remove, so the line offers the screenshot (and a
 * retry where the cause can pass) and no removal. A cause whose line names no
 * removal keeps its one line.
 */
const REFUSED_LINE_BY_CODE: Readonly<Partial<Record<RuleCode, RuleCode>>> = {
  page_capture_too_large: "page_capture_refused_too_large",
  page_capture_changed: "page_capture_refused_changed",
  page_capture_http_error: "page_capture_refused_http_error",
  page_capture_bot_wall: "page_capture_refused_bot_wall",
  page_capture_empty: "page_capture_refused_empty",
  page_capture_other_page: "page_capture_refused_other_page",
  page_capture_timeout: "page_capture_refused_timeout",
  page_capture_unreachable: "page_capture_refused_unreachable",
  page_capture_failed: "page_capture_refused_failed",
};

export function refusedCaptureLineCode(reason: unknown): RuleCode {
  const code = pageCaptureLineCode(reason);
  return REFUSED_LINE_BY_CODE[code] ?? code;
}

/**
 * The readiness codes of the landing-page read's verdict that a page's
 * picture is not the page (`CAPTURE_SHOWS` other than `page`): the Save drops
 * such a picture (Nest `recordCaptureNotThePage`). A page that carries one has
 * the server's verdict on its link, so the link dialog offers no picture of
 * that same link (H08-LP); a capture's own failure is told by the capture.
 */
export const NOT_THE_PAGE_CAPTURE_CODES: ReadonlyArray<RuleCode> = CAPTURE_SHOWS.filter(
  (shows) => shows !== "page",
).map((shows) => pageCaptureLineCode(shows));
